import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";

// Realistic EAKC response matching the actual UI
const RESPONSE_LINES = [
  { text: "The EAKC (Enterprise AI Knowledge Chat) platform is a secure,", hasSource: false },
  { text: "multi-tenant AI system designed for unified organizational", hasSource: false },
  { text: "knowledge management. It enables organizations to interact with", hasSource: false },
  { text: "their knowledge base through a chat interface, integrating data", hasSource: true, sourceNum: 2 },
  { text: "from various sources such as GitHub, Jira, Confluence, Slack,", hasSource: false },
  { text: "and documents. The platform leverages Retrieval-Augmented", hasSource: false },
  { text: "Generation (RAG) to provide contextually relevant answers", hasSource: true, sourceNum: 1 },
  { text: "grounded in indexed organizational content.", hasSource: false },
];

// Actual EAKC source files from the screenshot
const SOURCES = [
  { num: 1, file: "src/lib/rag/map-reduce-response.ts", preview: "// Reduce phase: merge partial answers...", match: "100%" },
  { num: 2, file: "src/app/layout.tsx", preview: "const inter = Inter({ subsets: ['latin'] })", match: "100%" },
  { num: 3, file: "src/lib/rag/entry-point-tracer.ts", preview: "/** Trace imports from entry points...", match: "100%" },
  { num: 4, file: "src/lib/rag/graph-augmented-retrieval.ts", preview: "/** Graph-Augmented Retrieval Module...", match: "100%" },
  { num: 5, file: "src/lib/mcp/tools.ts", preview: "// MCP tool definitions for query_eakc...", match: "100%" },
  { num: 6, file: "src/lib/mcp/tools-read.ts", preview: "/** EAKC MCP Read-Only Tool Impl...", match: "100%" },
];

export const Scene7Generation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });

  const exitOpacity = interpolate(frame, [190, 210], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Streaming text
  const totalChars = RESPONSE_LINES.reduce((sum, l) => sum + l.text.length, 0);
  const charsRevealed = Math.floor(
    interpolate(frame, [20, 110], [0, totalChars], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    }),
  );

  let charCount = 0;

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.04} />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 30,
          left: "50%",
          transform: "translate(-50%, 0)",
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
          zIndex: 10,
        }}
      >
        <div style={{ fontSize: 36, fontFamily: FONTS.heading, fontWeight: 700, color: COLORS.textPrimary }}>
          Step 8:{" "}
          <span style={{ color: COLORS.accentOrange }}>Synthesis &amp; Grounding</span>
          <span style={{ fontSize: 18, fontFamily: FONTS.mono, color: COLORS.textMuted, marginLeft: 12 }}>
            strict-source · gemini-2.5-flash
          </span>
        </div>
      </div>

      {/* === EAKC UI Mockup === */}
      <div
        style={{
          position: "absolute",
          top: 90,
          left: 60,
          right: 60,
          bottom: 50,
          borderRadius: 16,
          background: "#0D1321",
          border: `1px solid ${COLORS.borderLight}`,
          overflow: "hidden",
          display: "flex",
        }}
      >
        {/* Main Chat Area */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <div
            style={{
              padding: "12px 24px",
              borderBottom: `1px solid ${COLORS.border}`,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 14 }}>📋</span>
            <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textPrimary, fontWeight: 500 }}>
              EAKC Platform RAG Code Flow
            </span>
            <div style={{ marginLeft: "auto", display: "flex", gap: 12 }}>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 12,
                  color: COLORS.accentGreen,
                  padding: "4px 14px",
                  borderRadius: 6,
                  background: `${COLORS.accentGreen}15`,
                  border: `1px solid ${COLORS.accentGreen}30`,
                  fontWeight: 600,
                }}
              >
                Sources
              </span>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, padding: "20px 28px", overflow: "hidden" }}>
            {/* User query */}
            <div
              style={{
                maxWidth: 650,
                marginLeft: "auto",
                padding: "14px 20px",
                borderRadius: 12,
                background: `${COLORS.accentBlue}12`,
                border: `1px solid ${COLORS.accentBlue}20`,
                fontFamily: FONTS.body,
                fontSize: 13,
                color: COLORS.textPrimary,
                lineHeight: 1.6,
                marginBottom: 24,
              }}
            >
              What is the EAKC platform and what are the latest context for 4th of April 2026 and 31st of March 2026? Also explain the code flow of RAG implementation.
            </div>

            {/* AI Response */}
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              {/* Avatar */}
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${COLORS.accentBlue}, ${COLORS.accentPurple})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  flexShrink: 0,
                  marginTop: 4,
                }}
              >
                🤖
              </div>

              <div style={{ flex: 1 }}>
                {/* Heading */}
                <div
                  style={{
                    fontFamily: FONTS.heading,
                    fontSize: 20,
                    fontWeight: 700,
                    color: COLORS.textPrimary,
                    marginBottom: 14,
                    opacity: interpolate(
                      spring({ frame: frame - 18, fps, config: SPRING.snappy }),
                      [0, 1],
                      [0, 1],
                    ),
                  }}
                >
                  Overview of the EAKC Platform
                </div>

                {/* Streaming body */}
                <div
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 15,
                    color: COLORS.textPrimary,
                    lineHeight: 1.9,
                  }}
                >
                  {RESPONSE_LINES.map((line, i) => {
                    const lineStart = charCount;
                    charCount += line.text.length;

                    const visibleChars = Math.max(0, Math.min(line.text.length, charsRevealed - lineStart));
                    const visibleText = line.text.slice(0, visibleChars);
                    const isComplete = visibleChars >= line.text.length;

                    if (visibleChars === 0) return null;

                    return (
                      <span key={i}>
                        {visibleText}
                        {line.hasSource && isComplete && (
                          <span
                            style={{
                              fontFamily: FONTS.mono,
                              fontSize: 11,
                              color: COLORS.accentGreen,
                              background: `${COLORS.accentGreen}15`,
                              padding: "1px 5px",
                              borderRadius: 4,
                              marginLeft: 3,
                              fontWeight: 600,
                              verticalAlign: "super",
                            }}
                          >
                            {line.sourceNum}
                          </span>
                        )}
                        {" "}
                      </span>
                    );
                  })}
                  {charsRevealed < totalChars && frame % 16 < 10 && (
                    <span style={{ color: COLORS.accentOrange }}>▊</span>
                  )}
                </div>

                {/* Streaming indicator */}
                {charsRevealed < totalChars && (
                  <div
                    style={{
                      marginTop: 12,
                      fontFamily: FONTS.mono,
                      fontSize: 11,
                      color: COLORS.accentOrange,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <div
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: COLORS.accentOrange,
                        opacity: frame % 30 < 15 ? 1 : 0.3,
                      }}
                    />
                    Streaming via SSE...
                  </div>
                )}

                {/* Claim grounding + citation guard — run once synthesis completes */}
                {charsRevealed >= totalChars &&
                  (() => {
                    const gAppear = spring({ frame: frame - 120, fps, config: SPRING.snappy });
                    return (
                      <div
                        style={{
                          marginTop: 16,
                          display: "flex",
                          gap: 10,
                          flexWrap: "wrap",
                          opacity: interpolate(gAppear, [0, 1], [0, 1]),
                          transform: `translateY(${interpolate(gAppear, [0, 1], [10, 0])}px)`,
                        }}
                      >
                        {[
                          { t: "claim grounding ✓", c: COLORS.accentGreen },
                          { t: "citation guard ✓ — no fabricated [Source N]", c: COLORS.accentTeal },
                        ].map((b, i) => (
                          <span
                            key={i}
                            style={{
                              fontFamily: FONTS.mono,
                              fontSize: 12,
                              color: b.c,
                              padding: "6px 12px",
                              borderRadius: 8,
                              background: `${b.c}15`,
                              border: `1px solid ${b.c}35`,
                            }}
                          >
                            {b.t}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div
            style={{
              padding: "10px 24px",
              borderTop: `1px solid ${COLORS.border}`,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {["Chat", "Research", "Battle Card"].map((tab, i) => (
              <span
                key={i}
                style={{
                  padding: "4px 12px",
                  borderRadius: 12,
                  background: i === 0 ? COLORS.accentBlue : COLORS.bgCard,
                  fontFamily: FONTS.body,
                  fontSize: 11,
                  color: i === 0 ? "#fff" : COLORS.textMuted,
                }}
              >
                {tab}
              </span>
            ))}
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              {["Fast", "Balanced", "Deep"].map((m, i) => (
                <span
                  key={i}
                  style={{
                    padding: "3px 8px",
                    borderRadius: 4,
                    background: i === 2 ? `${COLORS.accentRed}25` : COLORS.bgCard,
                    fontFamily: FONTS.mono,
                    fontSize: 10,
                    color: i === 2 ? COLORS.accentRed : COLORS.textMuted,
                  }}
                >
                  {m}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Sources Panel */}
        <div
          style={{
            width: 380,
            borderLeft: `1px solid ${COLORS.border}`,
            padding: "16px",
            overflowY: "hidden",
          }}
        >
          {/* Sources header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 16 }}>📑</span>
              <span style={{ fontFamily: FONTS.heading, fontSize: 16, fontWeight: 700, color: COLORS.textPrimary }}>
                Sources (18)
              </span>
            </div>
            <span style={{ fontFamily: FONTS.mono, fontSize: 14, color: COLORS.textMuted, cursor: "pointer" }}>✕</span>
          </div>

          {/* Source items */}
          {SOURCES.map((source, i) => {
            const srcDelay = 30 + i * 18;
            const srcAppear = spring({ frame: frame - srcDelay, fps, config: SPRING.snappy });

            return (
              <div
                key={i}
                style={{
                  padding: "12px 14px",
                  marginBottom: 10,
                  borderRadius: 10,
                  background: COLORS.bgCard,
                  border: `1px solid ${COLORS.border}`,
                  opacity: interpolate(srcAppear, [0, 1], [0, 1]),
                  transform: `translateX(${interpolate(srcAppear, [0, 1], [15, 0])}px)`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      background: `${COLORS.accentBlue}20`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: FONTS.mono,
                      fontSize: 11,
                      fontWeight: 700,
                      color: COLORS.accentBlue,
                    }}
                  >
                    {source.num}
                  </div>
                  <span
                    style={{
                      fontFamily: FONTS.mono,
                      fontSize: 12,
                      color: COLORS.accentBlue,
                      fontWeight: 500,
                    }}
                  >
                    {source.file}
                  </span>
                </div>
                <div
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 11,
                    color: COLORS.textMuted,
                    lineHeight: 1.4,
                    marginBottom: 6,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  {source.preview}
                </div>
                <span
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 10,
                    color: COLORS.accentGreen,
                    fontWeight: 600,
                  }}
                >
                  {source.match} match
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom stats — outside the mockup */}
      <div
        style={{
          position: "absolute",
          bottom: 10,
          left: "50%",
          transform: "translate(-50%, 0)",
          display: "flex",
          gap: 60,
        }}
      >
        {[
          { label: "Transport", value: "SSE Streaming", color: COLORS.accentOrange, delay: 120 },
          { label: "Grounding", value: "strict-source", color: COLORS.accentGreen, delay: 130 },
          { label: "Model", value: "gemini-2.5-flash", color: COLORS.accentPurple, delay: 140 },
        ].map((stat, i) => {
          const sAppear = spring({ frame: frame - stat.delay, fps, config: SPRING.snappy });
          return (
            <div key={i} style={{ textAlign: "center", opacity: interpolate(sAppear, [0, 1], [0, 1]) }}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 16, fontWeight: 700, color: stat.color }}>
                {stat.value}
              </div>
              <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
                {stat.label}
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
